import { useState, useEffect } from 'react';
import { Button } from '@/components';
import { useModalClose, useEnterSubmit } from '@/common/hooks';

export function CRMLostReasonModal({ isOpen, onClose, onSubmit, isPending }) {
  const [lostReason, setLostReason] = useState('');

  useEffect(() => {
    setLostReason('');
  }, [isOpen]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!lostReason.trim()) return;
    onSubmit(lostReason);
  };

  const modalRef = useModalClose({
    isOpen,
    onClose,
    isDirty: lostReason !== '',
  });

  const formRef = useEnterSubmit({
    onSubmit: handleSubmit,
    isPending: isPending,
  });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      ref={formRef}
    >
      <div
        ref={modalRef}
        className="w-full max-w-[500px] max-h-[calc(100vh-2rem)] sm:max-h-[90vh] bg-white border border-[#EBF4EA] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
      >
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#EBF4EA] shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-[#1F2241] tracking-tight">
            Mark As Lost
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.75"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form
          id="lost-reason-form"
          onSubmit={handleSubmit}
          className="flex flex-col p-4 sm:p-6 gap-2 overflow-y-auto custom-scrollbar"
        >
          <label className="text-sm font-semibold text-text-secondary">
            Reason For Losing This Lead <span className="text-red-500">*</span>
          </label>
          <textarea
            autoFocus
            rows={4}
            className="w-full resize-none bg-[#F5F5F5] px-3 py-2.5 sm:px-4 sm:py-3 text-sm rounded-xl border-0 text-[#1F2241] focus:ring-[3px] focus:ring-primary/20 outline-none transition-all placeholder:text-[#868686]"
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            placeholder="e.g. Budget constraints, went with competitor..."
            required
          />
        </form>

        <div className="p-4 sm:p-6 border-t border-[#EBF4EA] flex justify-end shrink-0">
          <Button
            type="submit"
            variant="primary"
            form="lost-reason-form"
            disabled={isPending || !lostReason.trim()}
            className="h-10 px-6 text-sm font-semibold"
          >
            {isPending ? (
              <svg
                className="animate-spin h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <>
                Confirm Lost
                <span aria-hidden>→</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
